import { KeyValue } from '@angular/common';
import { ChangeDetectorRef, Directive, inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import Clarity from '@microsoft/clarity';
import { Base } from '../base';
import { AnnouncementKind, BannerMode } from '../definitions/announcement.definitions';
import { BadgeColor, BadgeKind } from '../definitions/badge.definitions';
import { ButtonGroupSize, ButtonKind, ButtonRole, ButtonSize, MenuButtonMode, MenuDivider } from '../definitions/button.definitions';
import { CalendarEventColorKind } from '../definitions/calendar.definitions';
import { ChartKind } from '../definitions/chart.definitions';
import { DropdownPanelKind } from '../definitions/dropdown-panel.definitions';
import { EmptyStateGraphic, EmptyStateSize } from '../definitions/empty-state.definitions';
import { FormContainerSize, FormFieldLayout, Orientation, TextKind } from '../definitions/forms/form.definitions';
import { FroalaGalleryMode, FroalaSize } from '../definitions/froala.definitions';
import { Icon, IconBulletAlignment, IconBulletTextStyle, IconSize } from '../definitions/icon.definitions';
import { IframeCssClass } from '../definitions/iframe.definitions';
import { SectionHeaderSize } from '../definitions/layout.definitions';
import { PopoverPosition } from '../definitions/popover.definitions';
import { ProfileImageSize } from '../definitions/profile-image.definitions';
import { ProgressBarKind } from '../definitions/progess.definitions';
import { StarRatingViewKind } from '../definitions/star-rating.definitions';
import { StatusKind } from '../definitions/status-label.definitions';
import { SubmissionBarKind } from '../definitions/submission-bar.definitions';
import { TabKind, TabSize } from '../definitions/tab.definitions';
import { UploadFileType } from '../definitions/upload.definitions';

@Directive()
export abstract class BaseViewComponent extends Base implements OnInit, OnDestroy {

    protected override readonly _ViewContainer = inject(ViewContainerRef);
    protected readonly _ChangeDetector = inject(ChangeDetectorRef);

    protected readonly ButtonSize = ButtonSize;
    protected readonly ButtonKind = ButtonKind;
    protected readonly IconSize = IconSize;
    protected readonly Icon = Icon;
    protected readonly StatusKind = StatusKind;
    protected readonly Orientation = Orientation;
    protected readonly ProgressBarKind = ProgressBarKind;
    protected readonly IframeCssClass = IframeCssClass;
    protected readonly ProfileImageSize = ProfileImageSize;
    protected readonly EmptyStateGraphic = EmptyStateGraphic;
    protected readonly AnnouncementKind = AnnouncementKind;
    protected readonly ChartKind = ChartKind;
    protected readonly CalendarEventColorKind = CalendarEventColorKind;
    protected readonly IconBulletAlignment = IconBulletAlignment;
    protected readonly IconBulletTextStyle = IconBulletTextStyle;
    protected readonly MenuButtonMode = MenuButtonMode;
    protected readonly MenuDivider = MenuDivider;
    protected readonly TextKind = TextKind;
    protected readonly FroalaSize = FroalaSize;
    protected readonly FroalaGalleryMode = FroalaGalleryMode;
    protected readonly UploadFileType = UploadFileType;
    protected readonly FormContainerSize = FormContainerSize;
    protected readonly TabSize = TabSize;
    protected readonly SubmissionBarKind = SubmissionBarKind;
    protected readonly BadgeKind = BadgeKind;
    protected readonly BadgeColor = BadgeColor;
    protected readonly PopoverPosition = PopoverPosition;
    protected readonly TabKind = TabKind;
    protected readonly DropdownPanelKind = DropdownPanelKind;
    protected readonly StarRatingViewKind = StarRatingViewKind;
    protected readonly ButtonRole = ButtonRole;
    protected readonly ButtonGroupSize = ButtonGroupSize;
    protected readonly BannerMode = BannerMode;
    protected readonly FormFieldLayout = FormFieldLayout;
    protected readonly SectionHeaderSize = SectionHeaderSize;
    protected readonly EmptyStateSize = EmptyStateSize;

    protected readonly notSpecifiedString = $localize`:@@CommonNotSpecifiedLabel:Not specified`;

    ngOnInit(): void {
        // Get the selector from Angular metadata. 
        const selector: string = ((this.constructor as any)?.['ɵcmp'] as any)?.selectors?.[0]?.[0];
        if (selector && !selector.startsWith('lib-') && !selector.startsWith('ng-')) {
            try {
                // Ignore errors with Clarity, e.g. init was never called like in the website
                Clarity?.setTag("Component", selector);
            } catch {}
        }
    }

    ngOnDestroy() {
        this.Destroy();
    }

    DetectChanges(): void {
        this._ChangeDetector.detectChanges();
    }

    CloseOpenFormFields(): void {
        const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
        });

        document.dispatchEvent(clickEvent);
    }

    // This can't be in definitions because we need to use it in the templates
    protected keepOrder<K, V>(_a: KeyValue<K, V>, _b: KeyValue<K, V>) {
        return 0;
    }
}
